"use client"

import { useState } from "react"
import { getMirrorPosts } from "lib/api/import"
import { ProcessedEntry } from "@/types/import"
import { ImportOptions } from "../../../types"

/**
 * Type definition for Mirror import stages
 */
type MirrorImportStage = "ENTER_WALLET" | "ACKNOWLEDGE" | "IMPORTING"

/**
 * Custom hook to manage Mirror import flow state
 * Handles the multi-stage process of importing from Mirror
 *
 * @param defaultEntries - Optional pre-loaded entries to start with
 * @param prefilledWallet - Optional wallet to use to prefill input
 * @returns Object containing state and functions for the Mirror import flow
 */
export default function useMirrorImportFlow(
  defaultEntries: ProcessedEntry[] = [],
  prefilledWallet?: `0x${string}`
) {
  // Track current stage of the import process
  const [stage, setStage] = useState<MirrorImportStage>(
    defaultEntries.length > 0 ? "ACKNOWLEDGE" : "ENTER_WALLET"
  )

  // Form inputs and loading state
  const [wallet, setWallet] = useState(prefilledWallet || "")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Posts data
  const [entries, setEntries] = useState<ProcessedEntry[]>(defaultEntries)
  const [notes, setNotes] = useState<any[]>([])

  // Import options
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    overwrite: false,
    storeOnArweave: false,
  })

  /**
   * Handles wallet address submission to retrieve Mirror posts
   */
  const handleWalletSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!wallet) return

    setError(null)
    setIsLoading(true)

    try {
      const fetchedEntries = await getMirrorPosts(wallet)

      if (!fetchedEntries || fetchedEntries.length === 0) {
        setError(
          "Unable to retrieve posts from Mirror's Arweave. Are you using the correct contributor wallet address?"
        )
        return
      }

      setEntries(fetchedEntries)
      setStage("ACKNOWLEDGE")
    } catch (err) {
      setError("Error fetching posts from Mirror")
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Stores processed notes and advances to importing stage
   */
  const handleContinueToImport = (processedNotes: any[]) => {
    setNotes(processedNotes)
    setStage("IMPORTING")
  }

  /**
   * Navigation handlers for each stage
   */
  const goBack = () => {
    if (stage === "ACKNOWLEDGE") {
      setStage("ENTER_WALLET")
    } else if (stage === "IMPORTING") {
      setStage("ACKNOWLEDGE")
    }
  }

  return {
    stage,
    wallet,
    setWallet,
    isLoading,
    error,
    entries,
    notes,
    importOptions,
    setImportOptions,
    handleWalletSubmit,
    handleContinueToImport,
    goBack,
  }
}
